<script lang="ts">
    import { cn, getClient, getHighlightSegments } from "../../lib/utils.js";
    import { m } from "../../lib/messages.svelte.js";
    import { Spinner } from "../../lib/components/ui/spinner";
    import { Badge } from "../../lib/components/ui/badge";
    import { Input } from "../../lib/components/ui/input";
    import * as Select from "../../lib/components/ui/select/index.js";
    import * as Table from "../../lib/components/ui/table/index.js";
    import TablePagination from "../../lib/components/common/table-pagination.svelte";
    import * as Sheet from "../../lib/components/ui/sheet/index.js";
    import ChevronUp from "@lucide/svelte/icons/chevron-up";
    import ChevronDown from "@lucide/svelte/icons/chevron-down";
    import ChevronsUpDown from "@lucide/svelte/icons/chevrons-up-down";
    import type { Res } from "@repo/backend/client";
    import { maybeFormatDate } from "../../lib/components/salesorder/utils/date-utils.js";
    import TransactionDetailSlideout from "./transaction-detail-slideout-v2.svelte";
    import type { InboxId } from "@repo/backend/types";

    const {
        from,
        to,
        inboxIds,
        partnerName,
    }: {
        from: string;
        to: string;
        inboxIds?: InboxId[];
        partnerName?: string;
    } = $props();

    type AdjustmentType = Res["Get dashboard transactions v2"][number]["adjustments"][number];
    type Transaction = Res["Get dashboard transactions v2"][number];

    const adjustmentTypes: AdjustmentType[] = [
        "CUSTOMER_CHANGED",
        "QUANTITY_CHANGED",
        "PRODUCT_CHANGED",
        "POSITION_ADDED",
        "POSITION_REMOVED",
        "PRODUCT_NOT_DETECTED",
    ];

    const pageSizeOptions = [10, 25, 50, 100] as const;
    let pageSize = $state<(typeof pageSizeOptions)[number]>(25);
    let pageNumber = $state(1);
    let searchInput = $state("");
    let searchQuery = $state("");
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;
    let statusFilter = $state<"all" | "submitted" | "pending">("all");
    let adjustmentFilters = $state<AdjustmentType[]>([]);
    let sortColumn = $state<keyof Transaction | null>(null);
    let sortDirection = $state<"asc" | "desc" | null>(null);
    let selectedTransaction = $state<Transaction | null>(null);
    let sheetOpen = $state(false);

    function openTransactionDetail(transaction: Transaction) {
        selectedTransaction = transaction;
        sheetOpen = true;
    }

    $effect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        const currentInput = searchInput;
        searchTimeout = setTimeout(() => {
            searchQuery = currentInput;
            pageNumber = 1;
        }, 400);
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    });

    async function fetchTransactions(
        from_: typeof from,
        to_: typeof to,
        inboxIds_: typeof inboxIds,
        partnerName_: typeof partnerName,
    ) {
        return await getClient()["Get dashboard transactions v2"]({
            query: {
                from: from_,
                to: to_,
                inboxIds: inboxIds_ && inboxIds_.length > 0 ? inboxIds_.join(",") : undefined,
                partnerName: partnerName_,
            },
        });
    }
    let promise = $derived(fetchTransactions(from, to, inboxIds, partnerName));
    $effect(() => {
        const id = setInterval(() => {
            promise = fetchTransactions(from, to, inboxIds, partnerName);
        }, 120_000);
        return () => {
            clearInterval(id);
        };
    });

    function handleSort(column: keyof Transaction) {
        if (sortColumn === column) {
            if (sortDirection === "asc") {
                sortDirection = "desc";
            } else if (sortDirection === "desc") {
                sortColumn = null;
                sortDirection = null;
            }
        } else {
            sortColumn = column;
            sortDirection = "asc";
        }
    }

    function toggleAdjustmentFilter(adj: AdjustmentType) {
        if (adjustmentFilters.includes(adj)) {
            adjustmentFilters = adjustmentFilters.filter(a => a !== adj);
        } else {
            adjustmentFilters = [...adjustmentFilters, adj];
        }
        pageNumber = 1;
    }
</script>

<div class="flex flex-col gap-4 overflow-hidden py-4">
    <div class="flex flex-wrap items-center gap-6">
        <div class="flex flex-col gap-1">
            <span class="text-muted-foreground text-sm">{m["dashboard.cases.search"]()}</span>
            <Input
                type="search"
                placeholder={m["dashboard.cases.searchPlaceholder"]()}
                class="w-64"
                bind:value={searchInput}
            />
        </div>
        <div class="flex flex-col gap-1">
            <span class="text-muted-foreground text-sm">{m["dashboard.cases.statusFilter"]()}</span>
            <Select.Root
                type="single"
                value={statusFilter}
                onValueChange={v => {
                    statusFilter = v as "all" | "submitted" | "pending";
                    pageNumber = 1;
                }}
            >
                <Select.Trigger class="w-35">
                    {statusFilter === "all"
                        ? m["dashboard.cases.statusAll"]()
                        : statusFilter === "submitted"
                          ? m["dashboard.cases.statusSubmitted"]()
                          : m["dashboard.cases.statusPending"]()}
                </Select.Trigger>
                <Select.Content>
                    <Select.Item value="all">{m["dashboard.cases.statusAll"]()}</Select.Item>
                    <Select.Item value="submitted">{m["dashboard.cases.statusSubmitted"]()}</Select.Item>
                    <Select.Item value="pending">{m["dashboard.cases.statusPending"]()}</Select.Item>
                </Select.Content>
            </Select.Root>
        </div>
        <div class="flex flex-col gap-1">
            <span class="text-muted-foreground text-sm">{m["dashboard.cases.adjustmentsFilter"]()}</span>
            <div class="flex h-9 flex-wrap items-center gap-1">
                {#each adjustmentTypes as adj (adj)}
                    <button
                        type="button"
                        class={cn(
                            "h-7 rounded-md border px-2 text-xs transition-colors",
                            adjustmentFilters.includes(adj)
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-input bg-background hover:bg-accent",
                        )}
                        onclick={() => toggleAdjustmentFilter(adj)}
                    >
                        {m[`dashboard.cases.adjustment.${adj}`]()}
                    </button>
                {/each}
            </div>
        </div>
    </div>

    {#await promise}
        <div class="flex flex-1 items-center justify-center">
            <Spinner class="size-8" />
            <span class="text-muted-foreground ml-2">{m["dashboard.cases.loading"]()}</span>
        </div>
    {:then data}
        {@const filteredData = (() => {
            let result = data;

            if (statusFilter !== "all") {
                result = result.filter(row => (statusFilter === "submitted" ? row.isSubmitted : !row.isSubmitted));
            }

            if (adjustmentFilters.length > 0) {
                result = result.filter(row => adjustmentFilters.some(adj => row.adjustments.includes(adj)));
            }

            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase().trim();
                result = result.filter(row => {
                    const status = row.isSubmitted
                        ? m["dashboard.cases.status.submitted"]()
                        : m["dashboard.cases.status.pending"]();
                    const searchable = [
                        row.emailSubject,
                        row.customerName,
                        row.orderReference,
                        row.erpTransactionNumber,
                        row.submittedBy,
                        status,
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();
                    return searchable.includes(query);
                });
            }

            return result;
        })()}
        {@const sortedData = (() => {
            if (!sortColumn || !sortDirection) return filteredData;

            const column = sortColumn;
            const direction = sortDirection;

            return [...filteredData].sort((a, b) => {
                const aVal = a[column];
                const bVal = b[column];

                if (aVal === null && bVal === null) return 0;
                if (aVal === null) return direction === "asc" ? 1 : -1;
                if (bVal === null) return direction === "asc" ? -1 : 1;

                if (typeof aVal === "string" && typeof bVal === "string") {
                    return direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }

                if (typeof aVal === "number" && typeof bVal === "number") {
                    return direction === "asc" ? aVal - bVal : bVal - aVal;
                }

                if (typeof aVal === "boolean" && typeof bVal === "boolean") {
                    return direction === "asc" ? (aVal ? 1 : 0) - (bVal ? 1 : 0) : (bVal ? 1 : 0) - (aVal ? 1 : 0);
                }

                return 0;
            });
        })()}
        {@const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize))}
        {@const clampedPageNumber = Math.max(1, Math.min(pageNumber, totalPages))}
        {@const paginatedData = (() => {
            const start = (clampedPageNumber - 1) * pageSize;
            const end = start + pageSize;
            return sortedData.slice(start, end);
        })()}
        <div class="flex-1 overflow-auto">
            <Table.Root>
                <Table.Header class="bg-background sticky top-0 z-10">
                    <Table.Row data-header-row>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("emailSubject")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.emailSubject"]()}
                                {@render sortIcon("emailSubject")}
                            </span>
                        </Table.Head>
                        <Table.Head class="min-w-42.5">
                            {m["dashboard.cases.columns.adjustments"]()}
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("isSubmitted")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.status"]()}
                                {@render sortIcon("isSubmitted")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("customerName")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.customer"]()}
                                {@render sortIcon("customerName")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("suggestedPositionCount")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.suggestedPositions"]()}
                                {@render sortIcon("suggestedPositionCount")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("submittedPositionCount")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.submittedPositions"]()}
                                {@render sortIcon("submittedPositionCount")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("orderReference")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.orderReference"]()}
                                {@render sortIcon("orderReference")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("erpTransactionNumber")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.erpTransactionNumber"]()}
                                {@render sortIcon("erpTransactionNumber")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("processedAt")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.processedAt"]()}
                                {@render sortIcon("processedAt")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("submittedAt")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.submittedAt"]()}
                                {@render sortIcon("submittedAt")}
                            </span>
                        </Table.Head>
                        <Table.Head
                            data-sortable
                            class="cursor-pointer select-none"
                            onclick={() => handleSort("submittedBy")}
                        >
                            <span class="inline-flex items-center gap-1">
                                {m["dashboard.cases.columns.submittedBy"]()}
                                {@render sortIcon("submittedBy")}
                            </span>
                        </Table.Head>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {#each paginatedData as row (row.suggestedTransactionId)}
                        <Table.Row class="cursor-pointer" onclick={() => openTransactionDetail(row)}>
                            <Table.Cell class="max-w-64">
                                <span class="block truncate" title={row.emailSubject ?? ""}>
                                    {@render highlightText(row.emailSubject, searchQuery)}
                                </span>
                            </Table.Cell>
                            <Table.Cell>
                                <div class="flex flex-wrap gap-1">
                                    {#each row.adjustments as adj (adj)}
                                        <Badge class="bg-orange-500 text-white hover:bg-orange-500">
                                            {m[`dashboard.cases.adjustment.${adj}`]()}
                                        </Badge>
                                    {/each}
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                <Badge
                                    variant="outline"
                                    class={row.isSubmitted
                                        ? "border-success-foreground text-success-foreground"
                                        : "border-warning-foreground text-warning-foreground"}
                                >
                                    {row.isSubmitted
                                        ? m["dashboard.cases.status.submitted"]()
                                        : m["dashboard.cases.status.pending"]()}
                                </Badge>
                            </Table.Cell>
                            <Table.Cell class="max-w-48">
                                <span class="block truncate" title={row.customerName ?? ""}>
                                    {@render highlightText(row.customerName, searchQuery)}
                                </span>
                            </Table.Cell>
                            <Table.Cell>
                                {row.suggestedPositionCount}
                            </Table.Cell>
                            <Table.Cell>
                                {row.submittedPositionCount ?? "-"}
                            </Table.Cell>
                            <Table.Cell class="max-w-40">
                                <span class="block truncate" title={row.orderReference ?? ""}>
                                    {@render highlightText(row.orderReference, searchQuery)}
                                </span>
                            </Table.Cell>
                            <Table.Cell>
                                {@render highlightText(row.erpTransactionNumber, searchQuery)}
                            </Table.Cell>
                            <Table.Cell>
                                {maybeFormatDate(row.processedAt, true) ?? "-"}
                            </Table.Cell>
                            <Table.Cell>
                                {maybeFormatDate(row.submittedAt, true) ?? "-"}
                            </Table.Cell>
                            <Table.Cell>
                                {@render highlightText(row.submittedBy, searchQuery)}
                            </Table.Cell>
                        </Table.Row>
                    {:else}
                        <Table.Row>
                            <Table.Cell colspan={11} class="h-24 text-center">
                                {m["dashboard.cases.noResults"]()}
                            </Table.Cell>
                        </Table.Row>
                    {/each}
                </Table.Body>
            </Table.Root>
        </div>

        <TablePagination
            {pageSizeOptions}
            {pageSize}
            pageNumber={clampedPageNumber}
            totalCount={sortedData.length}
            onPageSizeChange={size => {
                pageSize = size as (typeof pageSizeOptions)[number];
                pageNumber = 1;
            }}
            onPageChange={p => {
                pageNumber = p;
            }}
        />
    {:catch e}
        <div class="text-destructive flex flex-1 items-center justify-center">
            {e.message ?? "Error loading transactions"}
        </div>
    {/await}
</div>

<Sheet.Root bind:open={sheetOpen}>
    <Sheet.Content
        class="w-[80vw] max-w-none data-[state=open]:duration-200 sm:max-w-none"
        side="right"
        hideCloseButton
    >
        {#if selectedTransaction}
            <TransactionDetailSlideout transaction={selectedTransaction} />
        {/if}
    </Sheet.Content>
</Sheet.Root>

{#snippet highlightText(text: string | null | undefined, query: string)}
    {#each getHighlightSegments(text, query) as segment (segment.index)}
        {#if segment.highlight}
            <mark class="bg-yellow-200">{segment.text}</mark>
        {:else}
            {segment.text}
        {/if}
    {/each}
{/snippet}

{#snippet sortIcon(column: keyof Transaction)}
    {#if sortColumn === column && sortDirection === "asc"}
        <ChevronUp class="size-4 shrink-0" />
    {:else if sortColumn === column && sortDirection === "desc"}
        <ChevronDown class="size-4 shrink-0" />
    {:else}
        <ChevronsUpDown class="text-muted-foreground size-4 shrink-0" />
    {/if}
{/snippet}

<style>
    :global([data-header-row]:hover > th) {
        background-color: transparent !important;
    }
    :global([data-header-row] > th[data-sortable]:hover) {
        background-color: var(--muted) !important;
    }
</style>
